import React, { useRef, useState } from "react";
import styled from "styled-components";
import ButtonWithText from "app/popleads/components/ui-kit/Button";
import RichTextEditor from "app/popleads/components/ui-kit/RichTextEditor";
import { useDropzone } from "react-dropzone";
import { colors } from "app/popleads/components/ui-kit/colors";
import { toAbsoluteUrl } from "_metronic/_helpers";
import { decideFileType } from "../../activities/utils";
import SVG from "react-inlinesvg";
import { useExtensionContext } from "../ExtensionContext";
import useProfileNoteActions from "app/popleads/hooks/useProfileNoteActions";
import { toast } from "react-toastify";
import { ProfileLoadingOverlay } from "../ExtensionHome";
import { Spinner } from "react-bootstrap";
import { ExtensionBody } from "app/popleads/components/ui-kit/Text";

const StyledContainer = styled.div`
    position: relative;
    width: 100%;
    padding: 8px 12px;
    background-color: ${colors.white};
    border-top: 1px solid ${colors.neutral[100]};
`;

const StyledActionsContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 4px;
`;

const FileItemsWrapper = styled.div`
    max-height: 118px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 8px 0;
    padding: 1px 8px;
    box-sizing: border-box;
    overflow-y: auto;

    /* General scrollbar style */
    &::-webkit-scrollbar {
        width: 9px; /* Width of the scrollbar */
    }

    /* Scrollbar thumb style */
    &::-webkit-scrollbar-thumb {
        background-color: ${colors.neutral[500]}; /* Color of the scrollbar thumb */
        border-radius: 9px; /* Rounded corners */
        border: 3px solid transparent;
        background-clip: content-box;
        min-height: 32px;
    }

    /* Thumb color on hover */
    &::-webkit-scrollbar-thumb:hover {
        background-color: ${colors.neutral[700]};
    }

    /* Scrollbar corner style (if any) */
    &::-webkit-scrollbar-corner {
        background-color: ${colors.neutral[100]};
    }
`;

const FileItem = styled.div`
    display: flex;
    height: 23px;
    align-items: center;
    gap: 4px;
    user-select: none;
    & > span {
        color: #8b999f;
        font-size: 12px;
        font-weight: 400;
        line-height: 20px;
    }
`;

const DragWrapper = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${colors.neutral[25]};
    border: 1px dashed ${colors.primary[900]};
    opacity: 0.8;
    border-radius: 0px 0px 8px 8px;
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
`;

const ExtensionNotesFooter = ({ scrollToBottom }) => {
    const { profile, addProfileNote, refetchProfile, companyUsers } = useExtensionContext();
    const [note, setNote] = useState("");
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const { createNote } = useProfileNoteActions();
    const formRef = useRef(null);
    const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
        noClick: true,
        noKeyboard: true,
        accept: {
            "image/jpeg": [".jpg", ".jpeg"],
            "image/png": [".png"],
            "application/pdf": [".pdf"],
            "application/msword": [".doc"],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
            "application/vnd.ms-excel": [".xls"],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
            "text/plain": [".txt"],
            "video/mp4": [".mp4"],
            "text/csv": [".csv"],
            "image/svg+xml": [".svg"],
            "application/zip": [".zip"],
            "application/x-rar-compressed": [".rar"],
        },
        onDrop: (acceptedFiles) => {
            setSelectedFiles((prevFiles) =>
                [...prevFiles, ...acceptedFiles].map((file) =>
                    Object.assign(file, {
                        preview: URL.createObjectURL(file),
                    })
                )
            );
        },
        onDropRejected: () => {
            toast.error("Please upload only DOC, XLS, TXT, PDF, CSV, ZIP, RAR, MP4, JPEG, PNG, or SVG files");
        },
    });

    function uploadPlugin(editor) {
        editor.editing.view.document.on("clipboardInput", (event, data) => {
            const files = Array.from(data.dataTransfer.files).filter((file) => {
                const ext = file.name
                    .split(".")
                    .pop()
                    ?.toLowerCase();
                return (
                    file.type.startsWith("image/jpeg") ||
                    file.type.startsWith("image/png") ||
                    file.type.startsWith("application/pdf") ||
                    file.type.startsWith("application/msword") ||
                    file.type.startsWith("application/vnd.openxmlformats-officedocument.wordprocessingml.document") ||
                    file.type.startsWith("application/vnd.ms-excel") ||
                    file.type.startsWith("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") ||
                    file.type.startsWith("text/plain") ||
                    file.type.startsWith("video/mp4") ||
                    file.type.startsWith("text/csv") ||
                    file.type.startsWith("image/svg+xml") ||
                    file.type.startsWith("application/zip") ||
                    file.type.startsWith("application/x-zip-compressed") ||
                    file.type.startsWith("application/x-rar-compressed") ||
                    (file.type.startsWith("application/x-compressed") && (ext === "zip" || ext === "rar"))
                );
            });
            if (files.length !== data.dataTransfer.files.length) {
                toast.error("Please upload only DOC, XLS, TXT, PDF, CSV, ZIP, RAR, MP4, JPEG, PNG, or SVG files");
            }
            if (files.length > 0) {
                event.stop();
                setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
            }
        });
    }
    const mentionsFeed = companyUsers?.map((user) => {
        return {
            userId: user.id,
            text: user.name || user.ad,
            id: `@${user.name || user.ad}`,
        };
    });

    const editorConfig = {
        toolbar: ["bold", "italic", "bulletedList", "numberedList"],
        placeholder: selectedFiles.length > 0 ? "" : "Type your note (*Activate mention by typing '@')",
        extraPlugins: [uploadPlugin],
        mention: {
            feeds: [
                {
                    marker: "@",
                    feed: mentionsFeed || [],
                    minimumCharacters: 1,
                },
            ],
        },
        link: {
            addTargetToExternalLinks: true,
        },
    };

    const getUserIdByName = (userName) => {
        if (!userName || !companyUsers) return null;
        const cleanName = userName.replace("@", "");
        const user = companyUsers.find(
            (u) => (u.name || u.ad) === cleanName || (u.name || u.ad)?.toLowerCase() === cleanName.toLowerCase()
        );

        return user?.id || null;
    };

    const extractMentionIds = (text) => {
        try {
            const mentionIds = [];

            try {
                const dataRegex = /data-mention="([^"]+)"/g;
                let dataMatch;
                while ((dataMatch = dataRegex.exec(text)) !== null) {
                    const mentionText = dataMatch[1];
                    const userId = getUserIdByName(mentionText);

                    if (userId && !mentionIds.includes(userId.toString())) {
                        mentionIds.push(userId.toString());
                    }
                }
            } catch (e) {}

            if (mentionIds.length === 0) {
                try {
                    const userNames = companyUsers?.map((user) => user.name || user.ad).filter(Boolean) || [];
                    userNames.forEach((userName) => {
                        if (text.includes(`@${userName}`)) {
                            const user = companyUsers.find((u) => (u.name || u.ad) === userName);
                            if (user && user.id && !mentionIds.includes(user.id.toString())) {
                                mentionIds.push(user.id.toString());
                            }
                        }
                    });
                } catch (e) {}
            }

            if (mentionIds.length === 0) {
                try {
                    const mentionParser = new DOMParser();
                    const doc = mentionParser.parseFromString(text, "text/html");

                    const mentionSpans = doc.querySelectorAll(".mention");
                    mentionSpans.forEach((span) => {
                        const userName = span.textContent?.replace("@", "");
                        if (userName) {
                            const userId = getUserIdByName("@" + userName);
                            if (userId && !mentionIds.includes(userId.toString())) {
                                mentionIds.push(userId.toString());
                            }
                        }
                    });

                    // Düz metin içinde @UserName formatını ara
                    if (mentionIds.length === 0) {
                        const textContent = doc.body.textContent || "";
                        const mentionMatches = textContent.match(/@(\w+)/g);

                        if (mentionMatches && mentionMatches.length > 0) {
                            mentionMatches.forEach((match) => {
                                const userId = getUserIdByName(match);
                                if (userId && !mentionIds.includes(userId.toString())) {
                                    mentionIds.push(userId.toString());
                                }
                            });
                        }
                    }
                } catch (e) {
                    // Hata durumunda sessizce devam et
                }
            }

            return mentionIds;
        } catch (error) {
            return [];
        }
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);

            const editorHtml = note;
            const mentionIds = extractMentionIds(editorHtml);

            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = editorHtml;
            const plainText = tempDiv.innerHTML || tempDiv.textContent || tempDiv.innerText || "";

            let sendNoteObj = {
                profil: profile.id,
                not: plainText,
                isNotification: mentionIds.length > 0 ? 1 : 0,
            };

            if (selectedFiles && selectedFiles.length > 0) {
                const inputnames = [];
                selectedFiles.forEach((file, index) => {
                    if (file && file.name) {
                        inputnames.push(`note${index}`);
                        sendNoteObj[`note${index}`] = file;
                    }
                });
                if (inputnames.length > 0) {
                    sendNoteObj.inputName = inputnames;
                }
            }

            const result = await createNote(sendNoteObj);

            if (result.error) return;

            setNote("");
            setSelectedFiles([]);
            addProfileNote(result.data.not);

            if (selectedFiles.length > 0) {
                refetchProfile();
            }
            scrollToBottom();
        } catch (error) {
            toast.error(error.response?.data?.text);
        } finally {
            setLoading(false);
        }
    };

    const handleEditorReady = (editor) => {
        // Focus once the editor is ready.
        editor.editing.view.focus();
        editor.editing.view.document.on("keydown", (event, data) => {
            if (data.keyCode === 13) {
                if (!data.shiftKey) {
                    data.preventDefault();
                    if (formRef.current) {
                        formRef.current.dispatchEvent(new Event("submit", { cancelable: true }));
                    }
                } else {
                    const selection = editor.model.document.selection;
                    const currentPosition = selection.getLastPosition();
                    const currentBlock = currentPosition.parent;

                    if (currentBlock.name === "listItem" && currentBlock.isEmpty) {
                        editor.model.change((writer) => {
                            const paragraph = writer.createElement("paragraph");
                            writer.insert(paragraph, currentBlock, "after");
                            ///move cursor to new paragraph
                            writer.setSelection(paragraph, "in");
                            // Prevent the default behavior of the Enter key
                            data.domEvent.preventDefault();
                        });
                        // Scroll to the bottom of the editor
                        const editable = editor.ui.view.editable.element;
                        editable.scrollTop = editable.scrollHeight;
                        return;
                    }
                    if (
                        editor.model.document.selection.anchor.parent.name === "listItem" ||
                        editor.model.document.selection.anchor.parent.name === "paragraph"
                    ) {
                        editor.execute("enter");
                    }
                    // Scroll to the bottom of the editor
                    const editable = editor.ui.view.editable.element;
                    editable.scrollTop = editable.scrollHeight;
                    // Prevent the default behavior of the Enter key
                    data.domEvent.preventDefault();
                }
            }
        });
    };

    return (
        <>
            {loading && (
                <ProfileLoadingOverlay>
                    <Spinner animation="border" variant="primary" />
                </ProfileLoadingOverlay>
            )}
            <StyledContainer {...getRootProps()}>
                <input {...getInputProps()} />
                {isDragActive && (
                    <DragWrapper>
                        <span className="material-symbols-outlined">cloud_upload</span>
                        <ExtensionBody>Drop files here...</ExtensionBody>
                    </DragWrapper>
                )}
                {selectedFiles.length > 0 && (
                    <FileItemsWrapper>
                        {selectedFiles.map((file, index) => (
                            <FileItem key={index}>
                                <SVG src={toAbsoluteUrl(decideFileType(file?.name))} />
                                <span>{file.name}</span>
                                <span
                                    className="material-symbols-outlined"
                                    style={{ fontSize: "20px", cursor: "pointer" }}
                                    onClick={() => {
                                        setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
                                    }}
                                >
                                    close
                                </span>
                            </FileItem>
                        ))}
                    </FileItemsWrapper>
                )}
                <StyledActionsContainer>
                    <form ref={formRef} onSubmit={handleSubmit} style={{ width: "calc(100% - 88px)" }}>
                        <RichTextEditor
                            minHeight={"40px"}
                            maxHeight={"68px"}
                            data={note}
                            config={editorConfig}
                            onChange={(event, editor) => {
                                try {
                                    const editorData = editor.getData();
                                    setNote(editorData);
                                } catch (error) {}
                            }}
                            onReady={(editor) => {
                                try {
                                    handleEditorReady(editor);
                                } catch (error) {}
                            }}
                        />
                    </form>
                    <ButtonWithText
                        icon="attach_file"
                        iconType="material"
                        variant="neutral"
                        buttonStyle={"borderless"}
                        onClick={open}
                    />
                    <ButtonWithText
                        icon="send"
                        iconType="material"
                        variant={note?.length > 0 ? "neutral" : "passive"}
                        buttonStyle={"borderless"}
                        onClick={() => handleSubmit()}
                    />
                </StyledActionsContainer>
            </StyledContainer>
        </>
    );
};

export default ExtensionNotesFooter;
